import { ReactNode, useContext, useEffect, useState } from 'react';
import { appActions, AppContext } from 'context';
import { UserController } from 'networking/controllers/user-controller';
import {
  getQueryParamsObject,
  redictDependingFailingStatus,
} from 'helpers/utils';
import { Spinner } from 'common/spinner';
import { goToPage, RouteName } from 'routes';
import { LocalStorageApi } from 'helpers/local-storage';
import { injectQueryParamsIntoUrlPath } from 'routes/route-helpers';
import styles from './auth.module.scss';

type AuthProps = {
  optional?: boolean;
  children: ReactNode;
};

const Auth = ({ optional, children }: AuthProps) => {
  const [fetching, setFetching] = useState(true);
  const { state, dispatch } = useContext(AppContext);

  useEffect(
    () => () => {
      setFetching(false);
    },
    [],
  );

  const isUserGoingToVaultDetail = (userUrlPath: string) => {
    const pattern = /^\/vaults\/(\d+)(\/|$)/;

    const match = userUrlPath.match(pattern);
    return match ? match[1] : null;
  };

  const authenticate = async () => {
    if (state.logged) {
      setFetching(false);
      return;
    }

    try {
      const data = await UserController.me();
      dispatch({ type: appActions.USER_LOGGED, data });

      // If the route is only optionally authenticated, we don't need to redirect to the login page
      if (!optional) redictDependingFailingStatus(data.user);

      setFetching(false);
    } catch (e) {
      if (optional) {
        setFetching(false);
        return;
      }

      dispatch({ type: appActions.SESSION_EXPIRED });
      const queryParams = getQueryParamsObject();

      const userUrlPath = window.location.pathname;
      const userFullUrl = injectQueryParamsIntoUrlPath(
        userUrlPath,
        queryParams,
      );

      // A signed-out visitor to a vault page sees its public memories (and
      // gets there after signing in).
      const vaultId = isUserGoingToVaultDetail(userUrlPath);
      if (vaultId) {
        LocalStorageApi.set('previous-url', userFullUrl);
        goToPage(RouteName.VaultViewMode, { vaultId });
        return;
      }

      LocalStorageApi.set('previous-url', userFullUrl);
      goToPage(RouteName.SignIn);
    }
  };

  useEffect(() => {
    authenticate();
  }, []);

  if (fetching) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  if (state.logged || optional) {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{children}</>
    );
  }

  return null;
};

export { Auth };
